import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Avatar,
    Box,
    Grid,
    Divider,
    Button,
    Paper,
    Select,
    Typography,
    usePagination,
    Container,
    ClickAwayListener,
    MenuItem,
    MenuList,
    TextField,
    Grow,
    Popper,
    FormGroup,
    InputAdornment,
    FormControlLabel,
    Checkbox,
    Popover,
    OutlinedInput,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import Search from "@mui/icons-material/Search";
import { makeStyles } from "@mui/styles";
import * as Util from "../../../../services/global.service";
import cssVariables from "../../../../theme/cssVariables";
import { ReactComponent as ChevronExpandDown } from "../../../../assets/icons/chevron-expand-down.svg";
import { ReactComponent as ChevronExpandUp } from "../../../../assets/icons/chevron-expand-up.svg";
import { ReactComponent as FilterStatus } from "../../../../assets/icons/filter-status.svg";
import { ReactComponent as Nodata } from "../../../../assets/icons/nodata.svg";
import { ReactComponent as FilterIcon } from "../../../../icons/filter.svg"
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import Rating from '@mui/material/Rating';
import Stack from '@mui/material/Stack';
import StarIcon from '@mui/icons-material/Star';
import evaluateService from "../../../../services/evaluate.service";
import requestSent from "../../../../assets/images/request-sent.svg";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import { getOrganizationList } from '../../../../actions/global'

const styles = {
    path: {
        color: "#68717A",
    },
    paPlanning: {
        marginTop: "-12px",
        marginLeft: "19px",
    },
    icon: {
        color: "#6B6B6B",
        marginBottom: "-3px",
        marginRight: "10px",
    },
    lastToolBar: {
        marginTop: "0px",
    },
    blueBadge: {
        background: "rgba(93, 161, 150, 0.15)",
        padding: "5px 12px",
        color: "#5DA196",
        border: "1px solid #5DA196",
        borderRadius: "4px"
    },
    link: {
        color: "#5DA196",
        cursor: "pointer",
    },
    end: {
        color: "#A3A3A3",
    },
    colorText: {
        color: "#404349",
        fontWeight: "400",
        paddingLeft: "10px"
    },
    box: {
        width: "calc(50% - 30px)",
        flexDirection: "row",
        margin: "15px 15px",
    },
    content: {
        background: "#FFFFFF",
        "box-shadow": "0px 0px 5px rgba(0, 0, 0, 0.1)",
        "border-radius": "10px",
        padding: "17px 22px",
        height: "100%",
    },
    emty: {
        display: "flex",
        "flex-flow": "column wrap",
        "background-color": "#fff",
        height: "220px",
        "box-shadow": "0px 0px 5px rgb(0 0 0 / 10%)",
        "border-radius": "10px",
        justifyContent: "center",
        "align-items": "center",
    },
    twoContent: {
        display: "flex",
        justifyContent: "center",
        flexDirection: "row",
        "align-items": "center",
    },
    boxTwoContent: {
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "row",
        "align-items": "center",
    }
};

const useStyles = makeStyles({
    plusSvg: {
        "& > path": {
            fill: "#fff",
            stroke: "#fff",
        },
    },
    sortIcon: {
        "& > path": {
            fill: "#A0A0A0",
        },
    },
    draggingListItem: {
        background: 'rgb(235,235,235)'
    },
    toggleBtnRoot: {
        background: `transparent !important`,
        color: "#6B6B6B !important",
        width: "320px",
        border: "none !important",
    },
    toggleBtnSelected: {
        background: `${cssVariables.color.primary}!important`,
        color: "#fff!important",
    },
    flexPaper: {
        flex: 1,
        minWidth: 350,
        "box-shadow": "0px 2px 10px rgba(0, 0, 0, 0.1) !important",
        "border-radius": "10px !important        ",
        minHeight: "570px"
    },
    tab: {
        fontSize: "16px !important",
        fontWeight: "300 !important",
        // color: "#A3A3A3 !important",
        '&.Mui-selected': {
            color: cssVariables.color.primary + " !important"
        }
    },
    root: {
        display: 'flex',
        width: "100%",
        flexWrap: 'wrap',
        fontSize: "16px",
        lineHeight: "24px",
        fontWeight: "400",
    },
    iconContainer: {
        width: 40,
        height: 40,
        borderRadius: 5,
        marginTop: 3,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    profileContainer: {
        width: 40,
        height: 40,
        borderRadius: "50%",
        marginTop: 3,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    statusBadge: {
        width: "fit-content",
        minWidth: "120px",
        borderRadius: "5px",
        padding: "10px",
    },
    draftBg: {
        background: "#EFB76D26"
    },
    draftText: {
        color: "#EFB76D"
    },
    pendingBg: {
        background: "#F280261A"
    },
    pendingText: {
        color: "#F28026"
    },
    approveBg: {
        background: "#5DA19626"
    },
    approveText: {
        color: "#5DA196"
    },
    rejectedBg: {
        background: "#CC605826"
    },
    rejectedText: {
        color: "#CC6058"
    },
    notStartBg: {
        background: "#C4C4C41A"
    },
    notStartText: {
        color: "#C4C4C4",
    },
    cancelBg: {
        background: "#CC605826"
    },
    cancelText: {
        color: "#CC6058",
    },
    cancelPendingBg: {
        background: "#82828226"
    },
    cancelPendingText: {
        color: "#828282",
    },
    completedBg: {
        background: "rgba(93, 161, 150, 0.15);",
    },
    completedText: {
        color: "#5DA196",
    },
    blueBg: {
        background: "#6694CC",
    },
    purpleBg: {
        background: "#7580BD",
    },
    btnContainer: {
        width: "100%",
        display: "flex",
        alignItems: "flex-start",
        flexGrow: 1,
    },
    btnActive: {
        background: cssVariables.color.primary,
        color: `${cssVariables.color.white}!important`,
        "&:hover": {
            backgroundColor: "#4f8a80",
        },
    },
    btnInactive: {
        "&:hover": {
            backgroundColor: "#5da19626",
        },
    },
    btn: {
        transition: "0.2s",
        cursor: "pointer",
        borderRadius: "5px",
        padding: "10px",
        color: cssVariables.color.black,
    },
    paginationRoot: {
        color: `${cssVariables.color.primary}`,
        backgroundColor: "white!important",
        minWidth: "30px!important",
        padding: "5px!important",
    },
    paginationSelected: {
        color: "white!important",
        border: "1px solid rgba(93, 161, 150, 0.5)!important",
        backgroundColor: `${cssVariables.color.primary}!important`,
        minWidth: "30px!important",
        padding: "5px!important",
    },
    tableHover: {
        transition: "0.5s",
        "&:hover": {
            // background: "#4043490f",
            boxShadow: "3px 3px 5px rgba(0, 0, 0, 0.1)",
            cursor: "pointer",
        },
    },
    profileContainer: {
        width: 70,
        height: 70,
        borderRadius: "50%",
        marginTop: 3,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
});

function CustomPagination(props) {
    const { t } = useTranslation();
    const useStyles = makeStyles({
        paginationRoot: {
            color: `${cssVariables.color.primary}`,
            backgroundColor: "white!important",
            minWidth: "30px!important",
            padding: "5px!important",
        },
        paginationSelected: {
            color: "white!important",
            border: "1px solid rgba(93, 161, 150, 0.5)!important",
            backgroundColor: `${cssVariables.color.primary}!important`,
            minWidth: "30px!important",
            padding: "5px!important",
        },
    });
    const classes = useStyles();
    const List = styled("ul")({
        listStyle: "none",
        padding: 0,
        margin: 0,
        display: "flex",
    });
    const { items } = usePagination({
        count: props.count,
        showFirstButton: true,
        showLastButton: true,
    });
    return (
        <nav>
            <List>
                {items.length > 0 ? (
                    items.map(({ page, type, selected, ...item }, index) => {
                        let children = null;
                        selected = type === "page" && page === props.selected ? props.selected : selected
                        if (type === "start-ellipsis" || type === "end-ellipsis") {
                            children = "…";
                        } else if (type === "page") {
                            children = (
                                <Button
                                    {...item}
                                    size="small"
                                    type="button"
                                    style={{
                                        fontWeight: selected ? "bold" : undefined,
                                    }}
                                    variant="outlined"
                                    classes={{
                                        root: selected
                                            ? classes.paginationSelected
                                            : classes.paginationRoot,
                                    }}
                                    sx={{ margin: "0 5px" }}
                                >
                                    {page}
                                </Button>
                            );
                        } else if (type === "first" || type === "last") {
                            children = (
                                <Button
                                    {...item}
                                    disabled={type === "first" ? props.selected === 1 ? true : false : props.selected === props.count ? true : false}
                                    size="small"
                                    type="button"
                                    style={{
                                        fontWeight: selected ? "bold" : undefined,
                                    }}
                                    variant="outlined"
                                    classes={{
                                        root: selected
                                            ? classes.paginationSelected
                                            : classes.paginationRoot,
                                    }}
                                    sx={{ margin: "0 5px" }}
                                >
                                    {type === "first" ? <>&lt;&lt;</> : <>&gt;&gt;</>}
                                </Button>
                            );
                        } else {
                            children = (
                                <Button
                                    {...item}
                                    disabled={type === "previous" ? props.selected === 1 ? true : false : props.selected === props.count ? true : false}
                                    size="small"
                                    type="button"
                                    variant="outlined"
                                    classes={{
                                        root: classes.paginationRoot,
                                    }}
                                >
                                    {type === "previous"
                                        ? t("label.previous")
                                        : type === "next"
                                            ? t("label.next")
                                            : ""}
                                </Button>
                            );
                        }
                        return <li key={index}>{children}</li>;
                    })

                ) : (
                    <></>
                )}
                {props.setCurrentPage(items)}
            </List>
        </nav>
    );
}

function formatData(data) {
    let result = []

    if (data.out ?? false) {
        for (const key of Object.keys(data.out)) {
            let tmpList = []
            tmpList = data.out[key].map(item => {
                return {
                    serial: item.serial ?? "",
                    name: item.name ?? "",
                    evaluate_name: item.evaluate_name ?? "",
                    type: item.type ?? "",
                    start_date: item.start_date ?? "",
                    end_date: item.end_date ?? "",
                    photo: item.photo ?? "",
                    due_date: item.due_date ?? "",
                    status: item.status ?? "",
                }
            })
            result = [...result, ...tmpList]
        }
    }

    return result
}
const PerformanceEvaliation = (props) => {
    const Ratings = styled(Rating)({
        '& .MuiRating-icon': {
            color: '#F7B254 !important',
        },
    });
    const { t, i18n } = useTranslation();
    const _ = require("lodash");
    const [list, setList] = React.useState([]);
    const [filteredList, setFilteredList] = React.useState([]);
    const [itemsProgress, setItemsProgress] = React.useState([]);
    const [mainItemsProgress, setMainItemsProgress] = React.useState([]);
    const classes = useStyles();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [open, setOpen] = React.useState(false);
    const [opens, setOpens] = React.useState(false);
    const [opensForm, setOpensForm] = React.useState(false);
    const [perPage, setPerPage] = React.useState(10);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [totalPage, setTotalPage] = React.useState(1);
    const [checkResired, setCheckRsquired] = React.useState({});
    const [reSet, setreSet] = React.useState(false);
    const anchorRef = React.useRef(null);
    const anchorRefs = React.useRef(null);
    const anchorRefsForm = React.useRef(null);
    const [completed, setCompleted] = React.useState(true);
    const [progress, setProgress] = React.useState(true);
    const [notStarted, setNotStarted] = React.useState(true);
    const [search, setSearchValue] = React.useState("");
    const [searchFilterWord, setSearchFilterWord] = React.useState("");
    const [type, setType] = React.useState([]);
    const [checktype, setCheckType] = React.useState({});
    const [form, setForm] = React.useState([]);
    const [checkform, setCheckForm] = React.useState({});
    const [status, setStatus] = React.useState([]);
    const [checkstatus, setCheckStatus] = React.useState({});
    const [hasPrevious, setHasPrevious] = React.useState(false);
    const [cycleSelected, setCycleSelected] = React.useState(null);
    const [cycleList, setCycleList] = React.useState([])
    const [filtersCheckbox, setFiltersCheckbox] = React.useState([])
    const [filtersCheckboxList, setFiltersCheckboxList] = React.useState([])
    const [masterFiltersCheckboxList, setMasterFiltersCheckboxList] = React.useState([])
    const [filtersQuery, setFiltersQuery] = React.useState("")
    const [checkboxFiltersStatus, setCheckboxFiltersStatus] = React.useState("all")
    const [filterAnchor, setFilterAnchor] = React.useState(null)
    const [searchFilters, setSearchFilters] = React.useState("")
    const [firstVisit, setFirstVisit] = React.useState(true)
    const openFilters = Boolean(filterAnchor)
    const idFilters = openFilters ? 'filters-popover' : undefined


    const clickLink = (data) => {
        navigate(data, { replace: false });
    }


    const handleClickFilters = (event) => {
        setFilterAnchor(event.currentTarget)
    }

    const handleSearchFilters = (e) => {
        setSearchFilters(e.target.value)
    }

    const handleCloseFilters = () => {
        setFilterAnchor(null)
    }

    const handleFiltersCheckbox = (data) => {
        let tmpFiltersCheckbox = JSON.parse(JSON.stringify(filtersCheckbox))
        let tmpFiltersCheckboxList = [...filtersCheckboxList]
        if (data === "all") {
            if (checkboxFiltersStatus === "indeterminate" || checkboxFiltersStatus === "none") {
                let selectAllFilters = tmpFiltersCheckboxList.map(filter => filter.id)
                tmpFiltersCheckbox = selectAllFilters
            } else if (checkboxFiltersStatus === "all") {
                tmpFiltersCheckbox = []
            }
        } else {
            let isExisted = tmpFiltersCheckbox.find(item => item === data)
            if (isExisted) {
                tmpFiltersCheckbox = tmpFiltersCheckbox.filter(item => item !== data)
            } else if (!isExisted) {
                tmpFiltersCheckbox.push(data)
            }
        }
        setFiltersCheckbox(tmpFiltersCheckbox)
    }

    const handleGetList = (serialCycle) => {
        // if (Util.formatData(serialCycle, false) !== false) {
        //     evaluateService.listManageEvaluateCycle(serialCycle).then(
        //         (response) => {
        //             setMainItemsProgress(response?.data?.manage_evaluate !== undefined ? response?.data?.manage_evaluate : []);
        //             setItemsProgress(response?.data?.manage_evaluate !== undefined ? response?.data?.manage_evaluate : []);
        //             setForm(response?.data?.filters?.form !== undefined ? response?.data?.filters?.form : [])
        //             setStatus(response?.data?.filters?.status !== undefined ? response?.data?.filters?.status : [])
        //         },
        //     )
        // } else {
        evaluateService.listManageEvaluateCycle(serialCycle).then(
            (response) => {
                console.log('Response'.response?.data)
                setMainItemsProgress(response?.data?.manage_evaluate !== undefined ? response?.data?.manage_evaluate : []);
                setItemsProgress(response?.data?.manage_evaluate !== undefined ? response?.data?.manage_evaluate : []);
                setForm(response?.data?.filters?.form !== undefined ? response?.data?.filters?.form : [])
                setStatus(response?.data?.filters?.status !== undefined ? response?.data?.filters?.status : [])
                setHasPrevious(response?.data?.has_previous)
            },
        )
        // }
    };
    useEffect(() => {
        let sumData = {}
        for (let index = 0; index <= type.length - 1; index++) {
            sumData[type[index]] = true
        }
        setCheckType(sumData)
        dataSerachsProgress(mainItemsProgress)
    }, [type])

    useEffect(() => {
        let sumData = {}
        for (let index = 0; index <= form.length - 1; index++) {
            sumData[form[index]] = true
        }
        setCheckForm(sumData)
        dataSerachsProgress(mainItemsProgress)
    }, [form])

    useEffect(() => {
        let sumData = {}
        for (let index = 0; index <= status.length - 1; index++) {
            sumData[status[index]] = true
        }
        setCheckStatus(sumData)
        dataSerachsProgress(mainItemsProgress)
    }, [status])

    // go to detail page
    const handleGoToDetail = (serial, isNew, type) => {
        if (isNew === true) {
            evaluateService.logIsRead(serial).then(
                (response) => {
                    if (type === "probation") {
                        navigate("/kpi/probation-evaluation/" + serial, { replace: false });
                    } else {
                        navigate("/kpi/evaluation/" + serial, { replace: false });
                    }
                },
            )
        } else {
            if (type === "probation") {
                navigate("/kpi/probation-evaluation/" + serial, { replace: false });
            } else {
                navigate("/kpi/evaluation/" + serial, { replace: false });
            }
        }
    };

    function handleListKeyDown(event) {
        if (event.key === 'Tab') {
            event.preventDefault();
            setOpen(false);
        } else if (event.key === 'Escape') {
            setOpen(false);
        }
    }
    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }

        setOpen(false);
    };
    const handleCloses = (event) => {
        if (anchorRefs.current && anchorRefs.current.contains(event.target)) {
            return;
        }

        setOpens(false);
    };
    const handleClosesForm = (event) => {
        if (anchorRefsForm.current && anchorRefsForm.current.contains(event.target)) {
            return;
        }

        setOpensForm(false);
    };
    const filterType = (data) => {
        let result = []
        let dataFilter = JSON.parse(JSON.stringify(data))
        let datas = JSON.parse(JSON.stringify(checktype))
        let getObjectKey = Object.keys(datas)
        if (getObjectKey.length > 0) {
            for (let index = 0; index <= dataFilter.length - 1; index++) {
                if (datas[dataFilter[index]?.evaluate_type?.name] === true) {
                    result.push(dataFilter[index])
                }
            }
            filterForm(result)
        } else {
            filterForm(data)
        }
    };
    const filterForm = (data) => {
        let result = []
        let dataFilter = JSON.parse(JSON.stringify(data))
        let datas = JSON.parse(JSON.stringify(checkform))
        let getObjectKey = Object.keys(datas)
        if (getObjectKey.length > 0) {
            for (let index = 0; index <= dataFilter.length - 1; index++) {
                if (datas[dataFilter[index]?.form?.name] === true) {
                    result.push(dataFilter[index])
                }
            }
            filterStatus(result)
        } else {
            filterStatus(data)
        }
    };
    const filterStatus = (data) => {
        let result = []
        let dataFilter = JSON.parse(JSON.stringify(data))
        let datas = JSON.parse(JSON.stringify(checkstatus))
        let getObjectKey = Object.keys(datas)
        if (getObjectKey.length > 0) {
            for (let index = 0; index <= dataFilter.length - 1; index++) {
                if (datas[dataFilter[index]?.status?.name] === true) {
                    result.push(dataFilter[index])
                }
            }
            setItemsProgress(result)
        } else {
            setItemsProgress(data)
        }
        // let result = []
        // let dataFilter = JSON.parse(JSON.stringify(data))
        // if (completed === false && progress === false && notStarted === false) {
        //     setItemsProgress([])
        // } else if (completed === true && progress === true && notStarted === true) {
        //     setItemsProgress(data)
        // } else {
        //     if (completed === true && progress === true) {
        //         let resultData = dataFilter.filter(rs => rs?.status?.code === "completed" || rs?.status?.code === "in_progress");
        //         result = resultData
        //         setItemsProgress(result)
        //     } else if (progress === true && notStarted === true) {
        //         let resultData = dataFilter.filter(rs => rs?.status?.code === "not_started" || rs?.status?.code === "in_progress");
        //         result = resultData
        //         setItemsProgress(result)
        //     } else if (notStarted === true && completed === true) {
        //         let resultData = dataFilter.filter(rs => rs?.status?.code === "completed" || rs?.status?.code === "not_started");
        //         result = resultData
        //         setItemsProgress(result)
        //     } else {
        //         if (completed === true) {
        //             let resultData = dataFilter.filter(rs => rs?.status?.code === "completed");
        //             result = resultData
        //             setItemsProgress(result)
        //         } else if (progress === true) {
        //             let resultData = dataFilter.filter(rs => rs?.status?.code === "in_progress");
        //             result = resultData
        //             setItemsProgress(result)
        //         } else if (notStarted === true) {
        //             let resultData = dataFilter.filter(rs => rs?.status?.code === "not_started");
        //             result = resultData
        //             setItemsProgress(result)
        //         }
        //     }
        // }
    };

    const EditNotActive = (data) => {
        if (data === "completed") {
            setCompleted(!completed)
        } else if (data === "progress") {
            setProgress(!progress)
        } else if (data === "notStarted") {
            setNotStarted(!notStarted)
        } else if (data === "selectAll") {
            if (completed === true && progress === true && notStarted === true) {
                setCompleted(false)
                setProgress(false)
                setNotStarted(false)
            } else if (completed === false && progress === false && notStarted === false) {
                setCompleted(true)
                setProgress(true)
                setNotStarted(true)
            } else if (completed === true || progress === true || notStarted === true) {
                setCompleted(true)
                setProgress(true)
                setNotStarted(true)
            }
        }
    };
    const changeType = (typeSelect) => {
        let data = JSON.parse(JSON.stringify(checktype))
        if (typeSelect === "all") {
            let getObjectKey = Object.keys(data)
            let check = getObjectKey.filter((rs) => data[rs] === true)
            let sumData = {}
            if (check.length !== type.length) {
                for (let index = 0; index <= type.length - 1; index++) {
                    sumData[type[index]] = true
                }
            } else {
                for (let index = 0; index <= type.length - 1; index++) {
                    sumData[type[index]] = false
                }
            }
            setCheckType(sumData)
        } else {
            data[typeSelect] = !checktype[typeSelect]
            setCheckType(data)
        }
    };
    const changeForm = (formSelect) => {
        let data = JSON.parse(JSON.stringify(checkform))
        if (formSelect === "all") {
            let getObjectKey = Object.keys(data)
            let check = getObjectKey.filter((rs) => data[rs] === true)
            let sumData = {}
            if (check.length !== form.length) {
                for (let index = 0; index <= form.length - 1; index++) {
                    sumData[form[index]] = true
                }
            } else {
                for (let index = 0; index <= form.length - 1; index++) {
                    sumData[form[index]] = false
                }
            }
            setCheckForm(sumData)
        } else {
            data[formSelect] = !checkform[formSelect]
            setCheckForm(data)
        }
    };
    const changeStatus = (statusSelect) => {
        let data = JSON.parse(JSON.stringify(checkstatus))
        if (statusSelect === "all") {
            let getObjectKey = Object.keys(data)
            let check = getObjectKey.filter((rs) => data[rs] === true)
            let sumData = {}
            if (check.length !== status.length) {
                for (let index = 0; index <= status.length - 1; index++) {
                    sumData[status[index]] = true
                }
            } else {
                for (let index = 0; index <= status.length - 1; index++) {
                    sumData[status[index]] = false
                }
            }
            setCheckStatus(sumData)
        } else {
            data[statusSelect] = !checkstatus[statusSelect]
            setCheckStatus(data)
        }
    };
    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };
    const handleToggles = () => {
        setOpens((prevOpen) => !prevOpen);
    };
    const handleTogglesForm = () => {
        setOpensForm((prevOpen) => !prevOpen);
    };
    const handleSearchFilterWord = (event) => {
        setSearchFilterWord(event.target.value);
    };

    const currentData = useMemo(() => {
        const firstPageIndex = (currentPage - 1) * perPage;
        const lastPageIndex = firstPageIndex + perPage;
        return itemsProgress
            .slice(firstPageIndex, lastPageIndex);
    }, [itemsProgress, currentPage, perPage]);

    const handleTotalPage = () => {
        const List = itemsProgress
        const totalPage =
            Math.ceil(List.length / perPage) === 0
                ? 1
                : Math.ceil(List.length / perPage);
        setTotalPage(totalPage);
    };
    useEffect(() => {
        handleTotalPage();
    }, [itemsProgress, perPage]);

    useEffect(() => {
        let update = checkResired
        if (Object.keys(checkResired)[0] !== undefined) {
            if (update[Object.keys(checkResired)[0]] === false) {
                currentData.sort(function (b, a) {
                    if (a[Object.keys(checkResired)[0]] > b[Object.keys(checkResired)[0]]) {
                        return -1;
                    }
                    if (b[Object.keys(checkResired)[0]] > a[Object.keys(checkResired)[0]]) {
                        return 1;
                    }
                    return 0;
                });
                setreSet(!reSet)
            } else if (update[Object.keys(checkResired)[0]] === true) {
                currentData.sort(function (a, b) {
                    if (a[Object.keys(checkResired)[0]] > b[Object.keys(checkResired)[0]]) {
                        return -1;
                    }
                    if (b[Object.keys(checkResired)[0]] > a[Object.keys(checkResired)[0]]) {
                        return 1;
                    }
                    return 0;
                });
                setreSet(!reSet)
            }
        }
    }, [currentData]);

    useEffect(() => {
        dataSerachsProgress(mainItemsProgress)
    }, [completed, progress, notStarted, searchFilterWord, checktype, checkform, checkstatus, filtersCheckbox]);

    const dataSerachsProgress = async (data) => {
        let result = []
        let dataSerach = JSON.parse(JSON.stringify(data !== undefined && data !== null ? data : mainItemsProgress))

        //console.log('dataSerachsProgress', searchFilterWord, dataSerach.length, filtersCheckbox.length, dataSerach.length)

        if (dataSerach !== undefined && dataSerach.length > 0) {
            let resultData = dataSerach

            if (filtersCheckbox.length > 0) {
                resultData = dataSerach.filter(rs => {
                    return filtersCheckbox.indexOf(rs?.employee?.organization_id) !== -1
                })
            }

            if (searchFilterWord) {
                resultData = resultData.filter(rs => {

                    return Object.keys(rs).some(key => {
                        if (key === 'evaluate_name' || key === 'position' || key === 'name' || key === 'type') {
                            return rs[key].toString().toLowerCase().indexOf(searchFilterWord.toString().toLowerCase()) !== -1;
                        }
                        if (key === 'employee') {
                            let checkName = false;
                            if (rs[key].fullname !== undefined && rs[key].fullname.toString().toLowerCase().indexOf(searchFilterWord.toString().toLowerCase()) !== -1) {
                                checkName = true;
                            }
                            if (rs[key].position !== undefined && rs[key].position.toString().toLowerCase().indexOf(searchFilterWord.toString().toLowerCase()) !== -1) {
                                checkName = true;
                            }
                            return checkName;
                        }
                        if (key === 'evaluate_type') {
                            let checkNameType = false;
                            if (rs[key].name !== undefined && rs[key].name.toString().toLowerCase().indexOf(searchFilterWord.toString().toLowerCase()) !== -1) {
                                checkNameType = true;
                            }
                            return checkNameType;
                        }
                        if (key === 'form') {
                            let checkNameForm = false;
                            if (rs[key].name !== undefined && rs[key].name.toString().toLowerCase().indexOf(searchFilterWord.toString().toLowerCase()) !== -1) {
                                checkNameForm = true;
                            }
                            return checkNameForm;
                        }
                    });
                });
            }

            result = resultData
        } else {
            result = dataSerach;
        }

        console.log('result', result.length)


        await filterType(result);
    };

    const handlePerPageChange = (event) => {
        setPerPage(event.target.value);
    };
    const handleSetCurrentPage = (value) => {
        const getSelectedPage = value.filter(item => item.type === "page" && item.selected)
        setCurrentPage(getSelectedPage.length > 0 ? getSelectedPage[0].page : 1)
    };
    const toTimestamp = (strDate) => {
        if (strDate !== "" && strDate !== null && strDate !== undefined) {
            const dt = Date.parse(strDate);
            return dt / 1000;
        } else {
            return 0;
        }
    }
    let sortData = (key, check1) => {
        let update = checkResired
        if (check1 === false && update[key] !== check1) {
            if (key === "due_date") {
                currentData.sort(function (b, a) {
                    if (toTimestamp(a[key]) > toTimestamp(b[key])) {
                        return -1;
                    }
                    if (toTimestamp(b[key]) > toTimestamp(a[key])) {
                        return 1;
                    }
                    return 0;
                });
            } else if (key === "options") {
                currentData.sort(function (b, a) {
                    if (a[key].name_en > b[key].name_en) {
                        return -1;
                    }
                    if (b[key].name_en > a[key].name_en) {
                        return 1;
                    }
                    return 0;
                });
            } else if (key === "employee") {
                currentData.sort(function (b, a) {
                    if (a[key].fullname > b[key].fullname) {
                        return -1;
                    }
                    if (b[key].fullname > a[key].fullname) {
                        return 1;
                    }
                    return 0;
                });
            } else if (key === "evaluate_type") {
                currentData.sort(function (b, a) {
                    if (a[key].name > b[key].name) {
                        return -1;
                    }
                    if (b[key].name > a[key].name) {
                        return 1;
                    }
                    return 0;
                });
            } else if (key === "form") {
                currentData.sort(function (b, a) {
                    if (a[key].name > b[key].name) {
                        return -1;
                    }
                    if (b[key].name > a[key].name) {
                        return 1;
                    }
                    return 0;
                });
            } else {
                currentData.sort(function (b, a) {
                    if (a[key] > b[key]) {
                        return -1;
                    }
                    if (b[key] > a[key]) {
                        return 1;
                    }
                    return 0;
                });
            }
            update = { [key]: false }
            setCheckRsquired(update)
            setreSet(!reSet)
        } else if (check1 === true && update[key] !== check1) {
            if (key === "due_date") {
                currentData.sort(function (a, b) {
                    if (toTimestamp(a[key]) > toTimestamp(b[key])) {
                        return -1;
                    }
                    if (toTimestamp(b[key]) > toTimestamp(a[key])) {
                        return 1;
                    }
                    return 0;
                });
            } else if (key === "options") {
                currentData.sort(function (a, b) {
                    if (a[key].name_en > b[key].name_en) {
                        return -1;
                    }
                    if (b[key].name_en > a[key].name_en) {
                        return 1;
                    }
                    return 0;
                });
            } else if (key === "employee") {
                currentData.sort(function (a, b) {
                    if (a[key].fullname > b[key].fullname) {
                        return -1;
                    }
                    if (b[key].fullname > a[key].fullname) {
                        return 1;
                    }
                    return 0;
                });
            } else if (key === "evaluate_type") {
                currentData.sort(function (a, b) {
                    if (a[key].name > b[key].name) {
                        return -1;
                    }
                    if (b[key].name > a[key].name) {
                        return 1;
                    }
                    return 0;
                });
            } else if (key === "form") {
                currentData.sort(function (a, b) {
                    if (a[key].name > b[key].name) {
                        return -1;
                    }
                    if (b[key].name > a[key].name) {
                        return 1;
                    }
                    return 0;
                });
            } else {
                currentData.sort(function (a, b) {
                    if (a[key] > b[key]) {
                        return -1;
                    }
                    if (b[key] > a[key]) {
                        return 1;
                    }
                    return 0;
                });

            }
            update = { [key]: true }
            setCheckRsquired(update)
            setreSet(!reSet)
        }
    }

    let checkAll = () => {
        let check = true
        let data = checktype
        let getObjectKey = Object.keys(data)
        let checks = getObjectKey.filter((rs) => data[rs] === true)
        if (checks.length !== type.length) {
            check = false
        } else {
            check = true
        }
        return check;
    }
    let checkAllForm = () => {
        let check = true
        let data = checkform
        let getObjectKey = Object.keys(data)
        let checks = getObjectKey.filter((rs) => data[rs] === true)
        if (checks.length !== form.length) {
            check = false
        } else {
            check = true
        }
        return check;
    }
    let checkAllStatus = () => {
        let check = true
        let data = checkstatus
        let getObjectKey = Object.keys(data)
        let checks = getObjectKey.filter((rs) => data[rs] === true)
        if (checks.length !== status.length) {
            check = false
        } else {
            check = true
        }
        return check;
    }
    let checkIndeterminate = () => {
        let check = true
        let data = checktype
        let getObjectKey = Object.keys(data)
        let checksTrue = getObjectKey.filter((rs) => data[rs] === true)
        let checksFalse = getObjectKey.filter((rs) => data[rs] === false)
        if (getObjectKey.length !== checksTrue.length && getObjectKey.length !== checksFalse.length) {
            check = true
        } else {
            check = false
        }
        return check;
    }

    let checkIndeterminateForm = () => {
        let check = true
        let data = checkform
        let getObjectKey = Object.keys(data)
        let checksTrue = getObjectKey.filter((rs) => data[rs] === true)
        let checksFalse = getObjectKey.filter((rs) => data[rs] === false)
        if (getObjectKey.length !== checksTrue.length && getObjectKey.length !== checksFalse.length) {
            check = true
        } else {
            check = false
        }
        return check;
    }

    let checkIndeterminateStatus = () => {
        let check = true
        let data = checkstatus
        let getObjectKey = Object.keys(data)
        let checksTrue = getObjectKey.filter((rs) => data[rs] === true)
        let checksFalse = getObjectKey.filter((rs) => data[rs] === false)
        if (getObjectKey.length !== checksTrue.length && getObjectKey.length !== checksFalse.length) {
            check = true
        } else {
            check = false
        }
        return check;
    }
    const BootstrapTooltip = styled(({ className, ...props }) => (
        <Tooltip {...props} arrow classes={{ popper: className }} />
    ))(({ theme }) => ({
        [`& .${tooltipClasses.arrow}`]: {
            color: "#343A40",
        },
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: "#343A40",
            color: "#FFFFFF",
            padding: "11px 15px",
            fontSize: "14px"

        },
    }));
    const displayStepEmpty = (data) => {
        let options = []
        for (let index = 0; index <= data.total_step - 1; index++) {
            options.push(<Box sx={{ "border-radius": "50%", margin: "0 5px", width: "15px", height: "15px", background: "#F0F0F0" }}></Box>)
        }
        return (
            <Box sx={{ width: "170px", display: "flex", justifyContent: "flex-start", alignItems: "center" }} pr="15px">
                {options}
            </Box>
        );
    }
    const openNewTab = (val) => {
        const newWindow = window.open(val, '_blank', 'noopener,noreferrer')
        if (newWindow) newWindow.opener = null
    }

    const setDataTypeFilter = () => {
        let dataTypeFilter = []
        if (mainItemsProgress !== undefined && mainItemsProgress !== null && mainItemsProgress.length > 0) {
            for (let index = 0; index <= mainItemsProgress.length - 1; index++) {
                dataTypeFilter.push(mainItemsProgress[index]?.evaluate_type?.name)
            }
            setType([...new Set(dataTypeFilter)])
        }
    }

    const listCycle = async () => {

        evaluateService.cycle('annual').then((resp) => {
            if (resp.success) {

                if (resp?.data?.cycle && resp?.data?.cycle?.length > 0) {
                    setCycleList(resp?.data?.cycle)
                    setCycleSelected(resp?.data?.cycle[0]?.serial)
                }

            }
        })

    }

    const listOrganization = async (cycle_id) => {
        try {
            dispatch(getOrganizationList(cycle_id)).then((resp) => {
                let organizationFilters = resp?.organizations ?? []

                setFiltersCheckboxList(organizationFilters)
                setMasterFiltersCheckboxList(organizationFilters)

                let checkedAll = organizationFilters.map(filter => filter.id)
                setFiltersCheckbox(checkedAll)
            })
        } catch (error) {
            console.error('listOrganization', error)
        }
    }


    const checkfiltersCheckboxStatus = () => {
        let result = "none"
        if (filtersCheckbox.length === filtersCheckboxList.length) {
            result = "all"
        } else if (filtersCheckbox.length > 0 && filtersCheckbox.length < filtersCheckboxList.length) {
            result = "indeterminate"
        }
        setCheckboxFiltersStatus(result)
    }

    useEffect(() => {

        listCycle()

    }, [])

    useEffect(() => {
        if (cycleSelected) {
            listOrganization(cycleSelected)
            handleGetList(cycleSelected)
        }
    }, [cycleSelected]);

    useEffect(() => {
        setDataTypeFilter()
    }, [mainItemsProgress]);


    useEffect(() => {
        checkfiltersCheckboxStatus()
    }, [filtersCheckbox])


    return (
        <Box
            sx={{
                backgroundColor: "background.default",
                minHeight: "100%",
                py: 3,
            }}
        >
            <Container maxWidth="xl">
                <Grid container pt={2} pb={2}>
                    <Typography fontSize={16} lineHeight={1} color="#68717A"><span style={styles.link} onClick={() => clickLink('/kpi/home')}>{t('label.home')}</span> /  <span style={styles.end} >{t("sidebar.label.evaluationManage")}</span></Typography>
                </Grid>
                <Grid container spacing={2}>
                    <Grid container item >
                        <Grid item md={12} xs={12}>
                            <Box >
                                <Typography variant="h2" color="#404349" fontSize={36} lineHeight="40px">
                                    {t("sidebar.label.evaluationManage")}
                                </Typography>
                                <Typography color="#AAAAAA" pt="10px" fontSize={16} lineHeight="23.28px">
                                    {t("evaluation.manageEvaluationDescription")}
                                </Typography>
                                <Box sx={{ flex: 2 }}></Box>
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid item xs>
                        <Grid container display="flex" justifyContent="space-between" alignItems="center">

                            <Grid item md={5} xs={5}>
                                <Box sx={{ ".MuiButton-root": { padding: "6px 10px" }, display: "flex" }}>
                                    <TextField
                                        id="input-with-icon-textfield"
                                        fullWidth
                                        placeholder={t('label.filters')}
                                        size="small"
                                        sx={{ background: "#fff", width: "100%", "input": { padding: "11px 14px" }, ".MuiOutlinedInput-root": { color: "#404349", } }}
                                        className="searchTextField-h40"
                                        value={searchFilterWord}
                                        onChange={handleSearchFilterWord}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search style={{ color: "#C4C4C4" }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    {filtersCheckboxList.length > 0 &&
                                        <>
                                            <Button onClick={handleClickFilters} sx={{ display: "flex", height: "45px", "text-transform": "none", marginLeft: 2 }} variant="dashed-solid" ><Box sx={{ display: "flex", "align-items": "center", paddingLeft: 2, paddingRight: 2 }}>
                                                <FilterIcon style={{ width: '20px', height: '20px' }} />
                                                <Typography color="#404349">{t('label.filters')}</Typography></Box></Button>


                                            <Popover
                                                id={idFilters}
                                                open={openFilters}
                                                anchorEl={filterAnchor}
                                                onClose={handleCloseFilters}
                                                anchorOrigin={{
                                                    vertical: 'bottom',
                                                    horizontal: 'left',
                                                }}
                                            >
                                                {filtersCheckboxList ?
                                                    (<Box
                                                        sx={{
                                                            p: 2,
                                                        }}>
                                                        <Typography fontSize="16px" fontWeight="600" sx={{ my: 1 }}>
                                                            {t("label.organization")}
                                                        </Typography>
                                                        <TextField
                                                            id="search-filters"
                                                            fullWidth
                                                            placeholder={t("dashboardManager.label.searchPlaceholder")}
                                                            size="small"
                                                            sx={{
                                                                background: "#fff",
                                                                width: "100%",
                                                                input: { padding: "12px 14px", height: "10px" },
                                                                mb: 1
                                                            }}
                                                            className="searchTextField-h40"
                                                            value={searchFilters}
                                                            onChange={handleSearchFilters}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <Search style={{ color: "#C4C4C4" }} />
                                                                    </InputAdornment>
                                                                )
                                                            }}
                                                        />
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "flex-start",
                                                                flexDirection: "column",
                                                                pl: "4px",
                                                                pr: "4px"
                                                            }}>
                                                            <FormControlLabel
                                                                style={{ fontSize: '14px' }}
                                                                label={t("label.selectAll") + ' / ' + checkboxFiltersStatus}
                                                                control={
                                                                    <CustomCheckbox
                                                                        checked={checkboxFiltersStatus === 'all'}
                                                                        indeterminate={checkboxFiltersStatus === 'indeterminate'}
                                                                        onChange={() => handleFiltersCheckbox("all")}
                                                                    />
                                                                }
                                                            />
                                                            {
                                                                filtersCheckboxList.map((item) => {
                                                                    return (
                                                                        <FormControlLabel
                                                                            style={{ fontSize: '14px' }}
                                                                            label={item?.code + ': ' + item?.name}
                                                                            key={item?.id}
                                                                            control={<CustomCheckbox color="primary" checked={filtersCheckbox.includes(item?.id)} onChange={() => handleFiltersCheckbox(item?.id)} />}
                                                                        />
                                                                    )
                                                                })
                                                            }
                                                        </Box>
                                                    </Box>) : null
                                                }
                                            </Popover>
                                        </>
                                    }
                                </Box>
                            </Grid>


                            <Grid item md={5} xs={5} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                                <Select
                                    fullWidth
                                    className="custom-placeholder"
                                    displayEmpty
                                    labelId="cycleId"
                                    id="cycleId"
                                    input={<OutlinedInput size="small" />}
                                    value={cycleSelected}
                                    onChange={(e) => setCycleSelected(e.target.value)}
                                >
                                    {cycleList.map((option, idx) => {
                                        return <MenuItem key={idx} value={option.serial}><Typography fontWeight="400" fontSize='16px' color='#404349' component="span">{option.name}</Typography></MenuItem>
                                    })}
                                </Select>
                            </Grid>

                            {/* TESTING FEATURE */}
                            {
                                hasPrevious &&
                                (process.env.REACT_APP_MODE !== "uat" && process.env.REACT_APP_MODE !== "staging" && process.env.REACT_APP_MODE !== "production") &&
                                <Grid item md={2} xs={2} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                                    <Box sx={{ ".MuiButton-root": { padding: "6px 10px" }, display: "flex", justifyContent: "flex-end" }}>
                                        <Button onClick={() => openNewTab(`/kpi/performance-evaluation/previous`)} sx={{ display: "flex", height: "45px", "text-transform": "none" }} variant="dashed-solid" ><Box sx={{ display: "flex", "align-items": "center" }}><img style={{ display: "block", "margin-right": "10px" }} src={requestSent} alt="export"></img><Typography color="#404349">{t('evaluation.previousEvaluation')}</Typography></Box></Button>
                                    </Box>
                                </Grid>
                            }
                        </Grid>
                        <Grid container mt={2}>
                            <Grid container item >
                                <div className={classes.root}>
                                    <Paper className={classes.flexPaper} >
                                        <Box
                                            px="32px"
                                        >
                                            <Box
                                                px="25px"
                                                py="19px"
                                                sx={{ display: "flex", flexDirection: "row" }}
                                            >
                                                <Box sx={{ display: "flex", flexDirection: "row", width: "calc(26.42% - 28.33px)" }} pr="15px">
                                                    <Typography fontSize={16} lineHeight="23.96px" color="#A0A0A0" fontWeight="300px" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "max-content", "margin-right": "10px" }}>
                                                        {t("evaluation.employee")}
                                                    </Typography>
                                                    <Typography component="span" style={{ flexDirection: "column", justifyContent: "center", "display": "flex" }}>
                                                        <ChevronExpandUp style={{ cursor: "pointer" }} onClick={e => sortData("employee", true)} className={classes.sortIcon} />
                                                        <ChevronExpandDown style={{ cursor: "pointer" }} onClick={e => sortData("employee", false)} className={classes.sortIcon} />
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: "flex", flexDirection: "row", width: "calc(24.15% - 28.33px)" }} pr="15px">
                                                    <Typography fontSize={16} lineHeight="23.96px" color="#A0A0A0" fontWeight="300px" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "max-content", "margin-right": "10px" }}>
                                                        {/* {t("evaluation.formName")} */}
                                                        {t("evaluation.formNameManage")}
                                                    </Typography>
                                                    <Stack direction="row" spacing={2}>
                                                        <div>
                                                            <FilterStatus
                                                                ref={anchorRefsForm}
                                                                id="composition-buttons"
                                                                style={{ cursor: "pointer" }}
                                                                onClick={handleTogglesForm}
                                                            />
                                                            <Popper
                                                                open={opensForm}
                                                                anchorEl={anchorRefsForm.current}
                                                                role={undefined}
                                                                placement="bottom-start"
                                                                transition
                                                                disablePortal
                                                                style={{ "z-index": "2" }}
                                                            >
                                                                {({ TransitionProps, placement }) => (
                                                                    <Grow
                                                                        {...TransitionProps}
                                                                        style={{
                                                                            transformOrigin:
                                                                                placement === 'bottom-start' ? 'left top' : 'left bottom',
                                                                        }}
                                                                    >
                                                                        <Paper sx={{ "margin-top": "5px !important" }}>
                                                                            <ClickAwayListener onClickAway={handleClosesForm}>
                                                                                <MenuList
                                                                                    autoFocusItem={opensForm}
                                                                                    id="composition-menu"
                                                                                    aria-labelledby="composition-button"
                                                                                    onKeyDown={handleListKeyDown}
                                                                                >

                                                                                    <Box px="16px" py="6px">
                                                                                        {form !== undefined && form.length > 0 ?
                                                                                            <FormGroup>
                                                                                                <FormControlLabel control={<Checkbox indeterminate={checkIndeterminateForm()} onChange={e => changeForm("all")} checked={checkAllForm()} />} label={t('competency.selectAll')} />
                                                                                                {form.map((rs) => (
                                                                                                    <FormControlLabel control={<Checkbox onChange={e => changeForm(rs)} checked={checkform[rs]} />} label={rs} />
                                                                                                ))}
                                                                                            </FormGroup>
                                                                                            :
                                                                                            <MenuItem value="">
                                                                                                ---{t('label.notFund')}---
                                                                                            </MenuItem>
                                                                                        }

                                                                                    </Box>
                                                                                </MenuList>
                                                                            </ClickAwayListener>
                                                                        </Paper>
                                                                    </Grow>
                                                                )}
                                                            </Popper>
                                                        </div>
                                                    </Stack>
                                                </Box>
                                                <Box sx={{ display: "flex", flexDirection: "row", width: "calc(12.51% - 28.33px)" }} pr="15px">
                                                    <Typography fontSize={16} lineHeight="23.96px" color="#A0A0A0" fontWeight="300px" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "max-content", "margin-right": "10px" }}>
                                                        {t('evaluation.type')}
                                                    </Typography>
                                                    <Stack direction="row" spacing={2}>
                                                        <div>
                                                            <FilterStatus
                                                                ref={anchorRefs}
                                                                id="composition-buttons"
                                                                style={{ cursor: "pointer" }}
                                                                onClick={handleToggles}
                                                            />
                                                            <Popper
                                                                open={opens}
                                                                anchorEl={anchorRefs.current}
                                                                role={undefined}
                                                                placement="bottom-start"
                                                                transition
                                                                disablePortal
                                                                style={{ "z-index": "2" }}
                                                            >
                                                                {({ TransitionProps, placement }) => (
                                                                    <Grow
                                                                        {...TransitionProps}
                                                                        style={{
                                                                            transformOrigin:
                                                                                placement === 'bottom-start' ? 'left top' : 'left bottom',
                                                                        }}
                                                                    >
                                                                        <Paper sx={{ "margin-top": "5px !important" }}>
                                                                            <ClickAwayListener onClickAway={handleCloses}>
                                                                                <MenuList
                                                                                    autoFocusItem={opens}
                                                                                    id="composition-menu"
                                                                                    aria-labelledby="composition-button"
                                                                                    onKeyDown={handleListKeyDown}
                                                                                >

                                                                                    <Box px="16px" py="6px">
                                                                                        {type !== undefined && type.length > 0 ?
                                                                                            <FormGroup>
                                                                                                <FormControlLabel control={<Checkbox indeterminate={checkIndeterminate()} onChange={e => changeType("all")} checked={checkAll()} />} label={t('competency.selectAll')} />
                                                                                                {type.map((rs) => (
                                                                                                    <FormControlLabel control={<Checkbox onChange={e => changeType(rs)} checked={checktype[rs]} />} label={rs} />
                                                                                                ))}
                                                                                            </FormGroup>
                                                                                            :
                                                                                            <MenuItem value="">
                                                                                                ---{t('label.notFund')}---
                                                                                            </MenuItem>
                                                                                        }

                                                                                    </Box>
                                                                                </MenuList>
                                                                            </ClickAwayListener>
                                                                        </Paper>
                                                                    </Grow>
                                                                )}
                                                            </Popper>
                                                        </div>
                                                    </Stack>
                                                </Box>
                                                <Box sx={{ display: "flex", flexDirection: "row", width: "170px" }} pr="15px">
                                                    <Typography fontSize={16} lineHeight="23.96px" color="#A0A0A0" fontWeight="300px" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "max-content", "margin-right": "10px" }}>
                                                        {t('evaluation.step')}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: "flex", flexDirection: "row", width: "calc(13.68% - 28.33px)" }} pr="15px">
                                                    <Typography fontSize={16} lineHeight="23.96px" color="#A0A0A0" fontWeight="300px" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "max-content", "margin-right": "10px" }}>
                                                        {t('evaluation.dueDateManage')}
                                                        {/* {t('evaluation.dueDate')} */}
                                                    </Typography>
                                                    <Typography component="span" style={{ flexDirection: "column", justifyContent: "center", "display": "flex" }}>
                                                        <ChevronExpandUp style={{ cursor: "pointer" }} onClick={e => sortData("due_date", true)} className={classes.sortIcon} />
                                                        <ChevronExpandDown style={{ cursor: "pointer" }} onClick={e => sortData("due_date", false)} className={classes.sortIcon} />
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: "flex", flexDirection: "row", width: "calc(11.65% - 28.33px)" }} pr="15px">
                                                    <Typography fontSize={16} lineHeight="23.96px" color="#A0A0A0" fontWeight="300px" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "max-content", "margin-right": "10px" }}>
                                                        {t('evaluation.rating')}
                                                    </Typography>
                                                    <Typography component="span" style={{ flexDirection: "column", justifyContent: "center", "display": "flex" }}>
                                                        <ChevronExpandUp style={{ cursor: "pointer" }} onClick={e => sortData("evaluator_rating", true)} className={classes.sortIcon} />
                                                        <ChevronExpandDown style={{ cursor: "pointer" }} onClick={e => sortData("evaluator_rating", false)} className={classes.sortIcon} />
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: "flex", flexDirection: "row", width: "calc(11.59% - 28.33px)" }}>
                                                    <Typography fontSize={16} lineHeight="23.96px" color="#A0A0A0" fontWeight="300px" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "max-content", "margin-right": "10px" }}>
                                                        {t('label.formStatus')}
                                                    </Typography>
                                                    <Stack direction="row" spacing={2}>
                                                        <div>
                                                            <FilterStatus
                                                                ref={anchorRef}
                                                                id="composition-button"
                                                                style={{ cursor: "pointer" }}
                                                                onClick={handleToggle}
                                                            />
                                                            <Popper
                                                                open={open}
                                                                anchorEl={anchorRef.current}
                                                                role={undefined}
                                                                placement="bottom-start"
                                                                transition
                                                                disablePortal
                                                                style={{ "z-index": "2" }}
                                                            >
                                                                {({ TransitionProps, placement }) => (
                                                                    <Grow
                                                                        {...TransitionProps}
                                                                        style={{
                                                                            transformOrigin:
                                                                                placement === 'bottom-start' ? 'left top' : 'left bottom',
                                                                        }}
                                                                    >
                                                                        <Paper sx={{ "margin-top": "5px !important" }}>
                                                                            <ClickAwayListener onClickAway={handleClose}>
                                                                                <MenuList
                                                                                    autoFocusItem={open}
                                                                                    id="composition-menu"
                                                                                    aria-labelledby="composition-button"
                                                                                    onKeyDown={handleListKeyDown}
                                                                                >

                                                                                    <Box px="16px" py="6px">
                                                                                        {status !== undefined && status.length > 0 ?
                                                                                            <FormGroup>
                                                                                                <FormControlLabel control={<Checkbox indeterminate={checkIndeterminateStatus()} onChange={e => changeStatus("all")} checked={checkAllStatus()} />} label={t('competency.selectAll')} />
                                                                                                {status.map((rs) => (
                                                                                                    <FormControlLabel control={<Checkbox onChange={e => changeStatus(rs)} checked={checkstatus[rs]} />} label={rs} />
                                                                                                ))}
                                                                                            </FormGroup>
                                                                                            :
                                                                                            <MenuItem value="">
                                                                                                ---{t('label.notFund')}---
                                                                                            </MenuItem>
                                                                                        }
                                                                                    </Box>
                                                                                </MenuList>
                                                                            </ClickAwayListener>
                                                                        </Paper>
                                                                    </Grow>
                                                                )}
                                                            </Popper>
                                                        </div>
                                                    </Stack>
                                                </Box>

                                            </Box>
                                            <Divider width="100%" />
                                        </Box>
                                        {currentData !== undefined && currentData !== null && currentData.length > 0 ? currentData.map((row, index) => (
                                            <Box px="32px" key={index}>
                                                <Box sx={{ width: "100%", position: "relative", transition: "0.5s", "&:hover": { boxShadow: "3px 5px 5px rgba(0, 0, 0, 0.1)", cursor: "pointer", "z-index": "1" } }} key={row?.serial} onClick={() => handleGoToDetail(row?.serial, row?.is_new, row?.evaluate_template_type)} >
                                                    <Box
                                                        sx={{ minHeight: "96px", background: "#FFF", width: "100%", display: "flex", "align-items": "center" }}
                                                        px="25px" py="20px"

                                                    >
                                                        <Box sx={{ width: "calc(26.42% - 28.33px)" }} pr="15px" >

                                                            <Box
                                                                display="flex"
                                                                sx={{
                                                                    flexFlow: "column",
                                                                    textAlign: "left",
                                                                    // marginLeft: 2,
                                                                    position: "relative",
                                                                }}
                                                            >
                                                                {row?.is_new ? (
                                                                    <div
                                                                        style={{
                                                                            position: "absolute",
                                                                            top: "calc(50% - 5px)",
                                                                            left: "-25px",
                                                                            width: "10px",
                                                                            height: "10px",
                                                                            background: "#CC6058",
                                                                            borderRadius: "50%",
                                                                        }}
                                                                    ></div>
                                                                ) : (
                                                                    <></>
                                                                )}
                                                                <Box display="flex" alignItems="center">
                                                                    <Avatar
                                                                        src={Util.formatData(row?.employee?.photo, "")}
                                                                        sx={{
                                                                            cursor: "pointer",
                                                                            width: 40,
                                                                            height: 40,
                                                                        }}
                                                                    />
                                                                    <Box pl="10px" sx={{ maxWidth: "calc(100% - 40px)" }}>
                                                                        <Typography
                                                                            variant="h4"
                                                                            fontWeight={row?.is_new ? 500 : 400}
                                                                            fontSize={16}
                                                                            lineHeight="27px"
                                                                            color="#404349"
                                                                            sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}
                                                                        >
                                                                            {Util.formatData(row?.employee?.fullname, "")}
                                                                        </Typography>
                                                                        <Typography fontSize="16px" color="#AAAAAA" lineHeight="24px" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                            {Util.formatData(row?.employee?.position, "")}
                                                                        </Typography>
                                                                        {Util.formatData(row?.employee?.organization, false) !== false ?
                                                                            <Typography fontSize="16px" color="#AAAAAA" lineHeight="24px" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                                ({row?.employee?.organization})
                                                                            </Typography>
                                                                            :
                                                                            null
                                                                        }
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                        <Box sx={{ width: "calc(24.15% - 28.33px)" }} pr="15px" >
                                                            <BootstrapTooltip title={Util.formatData(row?.form?.name, "")} placement="top">
                                                                <Typography
                                                                    variant="h4"
                                                                    fontWeight={row?.is_new ? 500 : 400}
                                                                    fontSize={16}
                                                                    lineHeight="27px"
                                                                    color="#404349"
                                                                    sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}
                                                                >
                                                                    {Util.formatData(row?.form?.name, "")}
                                                                </Typography>
                                                            </BootstrapTooltip>
                                                            <Typography fontSize="16px" color="#AAAAAA" lineHeight="24px" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                {Util.dateMonthFormatAdmin(row?.form?.start_date)} - {Util.dateMonthFormatAdmin(row?.form?.end_date)}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ width: "calc(12.51% - 28.33px)" }} pr="15px">
                                                            <Typography fontWeight="400" fontSize={16} color="#404349" lineHeight="23.92px" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                {Util.formatData(row?.evaluate_type?.name, "")}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ width: "170px", display: "flex", justifyContent: "flex-start", alignItems: "center" }} pr="15px">
                                                            {Util.formatData(row?.step, false) !== false ?
                                                                Util.formatData(row?.step?.step, false) !== false && row?.step?.step.length > 0 ?
                                                                    row?.step?.step.map((rs, i) => (
                                                                        rs?.is_current === true ?
                                                                            <BootstrapTooltip title={rs?.name} placement="top">
                                                                                <Box key={i} sx={{ "border-radius": "50%", margin: "0 5px", width: "15px", height: "15px", background: rs?.is_current === true && rs?.status === "approve" ? "#5DA196" : rs?.is_current === true ? "#EFB76D" : rs?.status === "approve" ? "#5DA196" : "#F0F0F0" }}></Box>
                                                                            </BootstrapTooltip>
                                                                            :
                                                                            <Box key={i} sx={{ "border-radius": "50%", margin: "0 5px", width: "15px", height: "15px", background: rs?.is_current === true && rs?.status === "approve" ? "#5DA196" : rs?.is_current === true ? "#EFB76D" : rs?.status === "approve" ? "#5DA196" : "#F0F0F0" }}></Box>
                                                                    ))
                                                                    :
                                                                    // <Box  sx={{ "border-radius": "50%", margin: "0 5px", width: "15px", height: "15px", background:"#F0F0F0" }}></Box>
                                                                    displayStepEmpty(row?.step)
                                                                : null}
                                                        </Box>
                                                        <Box sx={{ display: "flex", flexDirection: "column", width: "calc(13.68% - 28.33px)" }} pr="15px">
                                                            <Typography fontWeight="400" fontSize={16} color="#404349" lineHeight="24px" sx={{ color: toTimestamp(new Date()) > toTimestamp(row?.due_date) && row?.status?.code !== "completed" ? "#CC6058" : "#404349", whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                {Util.dateDayFormatAdmin(row?.due_date, "")}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: "flex", flexDirection: "column", width: "calc(11.65% - 28.33px)" }} pr="15px">
                                                            {(row?.evaluator_rating || row?.evaluator_rating_percent) && row?.status?.code === "completed" ?
                                                                <Box sx={{ display: "flex", 'align-items': "center", }}>

                                                                    {Util.formatData(row?.evaluator_rating, false) !== false &&
                                                                        <Ratings fontSize={19} readOnly name="text-feedback" emptyIcon={<StarIcon fontSize="inherit" />} defaultValue={formatData(Number(row?.evaluator_rating).toFixed(1), 0)} max={1} />
                                                                    }
                                                                    <Box>
                                                                        {Util.formatData(row?.evaluator_rating, false) !== false &&
                                                                            <Typography fontSize={16} pl="5px" pr="5px" fontWeight="400">
                                                                                {Util.formatData(Number(Util.formatData(row?.evaluator_rating, "0")).toFixed(row?.score_decimal), "0")}
                                                                            </Typography>
                                                                        }
                                                                        {Util.formatData(row?.evaluator_rating_percent, false) !== false &&
                                                                            <Typography fontSize={16} pl="5px" pr="5px" fontWeight="400">
                                                                                {Util.formatData(Number(Util.formatData(row?.evaluator_rating_percent, "0")).toFixed(row?.score_decimal), "0")}%
                                                                            </Typography>
                                                                        }
                                                                    </Box>
                                                                </Box>
                                                                :
                                                                <Box sx={{ display: "flex", 'align-items': "center", }}>
                                                                    <Typography fontSize={18} pl="5px" pr="5px" fontWeight="400">
                                                                        {"-"}
                                                                    </Typography>
                                                                </Box>
                                                            }
                                                        </Box>
                                                        <Box sx={{ width: "calc(11.59% - 28.33px)" }}>
                                                            <Box py="8px" px="20px" sx={{ "max-width": "100%", width: "max-content", border: row?.status?.code === "not_started" ? "1px solid #DADADA" : "none", "border-radius": "5px", background: row?.status?.code === "in_progress" ? "#EFB76D1A" : row?.status?.code === "completed" ? "#5DA1961A" : "none" }} >
                                                                <Typography fontSize={16} lineHeight="24px" fontWeight={500} color={row?.status?.code === "in_progress" ? "#EFB76D" : row?.status?.code === "completed" ? "#5DA196" : "#DADADA"} sx={{ "text-align": "center", "white-space": "nowrap", width: "100%", overflow: "hidden", "text-overflow": "ellipsis" }}>
                                                                    {row?.status?.name}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                                <Divider width="100%" />
                                            </Box>
                                        ))
                                            :
                                            <Box sx={{ "padding-top": "50px", "text-align": "center", "padding-bottom": "500px" }}>
                                                <Nodata />
                                                <Typography color="#A0A0A0" fontWeight="500" fontSize={20} lineHeight="30px" >
                                                    {t('label.noDataFilter')}
                                                </Typography>
                                                <Typography color="#A0A0A0" fontWeight="400" fontSize={16} lineHeight="24px" >
                                                    {t('label.tryChangingFilter')}
                                                </Typography>
                                            </Box>
                                        }
                                    </Paper>
                                </div>
                            </Grid>
                            <Grid container mt={1} spacing={2}>
                                <Grid item xs sm md display="flex" alignItems="center">
                                    <Box mr={1}>
                                        <Typography variant="s14_fw400">{t("label.showMaxItems")}</Typography>
                                    </Box>
                                    <Select
                                        id="pagination"
                                        variant="outlined"
                                        defaultValue={10}
                                        value={perPage}
                                        onChange={handlePerPageChange}
                                        displayEmpty
                                        size="small"
                                        className="white-bg"
                                    >
                                        <MenuItem value={10}>
                                            <Typography style={{ fontWeight: "300" }}>10</Typography>
                                        </MenuItem>
                                        <MenuItem value={20}>
                                            <Typography style={{ fontWeight: "300" }}>20</Typography>
                                        </MenuItem>
                                        <MenuItem value={30}>
                                            <Typography style={{ fontWeight: "300" }}>30</Typography>
                                        </MenuItem>
                                    </Select>
                                </Grid>
                                <Grid item display="flex" alignItems="center" ml="auto">
                                    <Typography variant="s14_fw400">
                                        {t("label.page")} {currentPage} {t("label.of")} {totalPage}
                                    </Typography>
                                </Grid>
                                <Grid item ml="auto" display="flex" alignItems="center">
                                    <CustomPagination
                                        setCurrentPage={handleSetCurrentPage}
                                        selected={currentPage}
                                        count={totalPage}
                                    ></CustomPagination>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

            </Container>
        </Box>
    );
};
export default PerformanceEvaliation;
